import {computed, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core'
import {IConfigData, IInterestRow} from '../0-application/types'
import {DataService} from './data-service'
import {BaseService} from '@sparbanken-syd/sparbanken-syd-theme'
import {environment} from '../../environments/environment'
import {Subject, tap} from 'rxjs'

/**
 Binding discount here is just an array of signal numbers
 we need to find the correct value based on selected "binding"
 And yes, engineering at its worst.
 */
export const toColumn: Record<number, number> = {
  3: 0,
  12: 1,
  24: 2,
  36: 3,
  48: 4,
  60: 5,
  84: 6,
  120: 7
}

export const fromColumn: Record<number, number> = {
  0: 3,
  1: 12,
  2: 24,
  3: 36,
  4: 48,
  5: 60,
  6: 84,
  7: 120
}

/**
 * Minimum for a dummy service
 */
export interface IConfiguration {
  /**
   * The date of this config as Unix Epoch ms.
   */
  listInterest: (productId: string, binding: number) => number
  internalInterest: (productId: string, binding: number, ltv: number) => number
  bpsProfitLimit: Signal<number>
  internalInterestPrivateLoan: Signal<number>
}

/**
 * A dummy to use in classes so that we can provision them later.
 */
export class DummyConfiguration implements IConfiguration {
  public listInterest() {
    return 0
  }

  public internalInterest() {
    return 0
  }

  /* v8 ignore next */
  public bpsProfitLimit = signal(50)
  /* v8 ignore next */
  public internalInterestPrivateLoan = signal(1.44)
}

@Injectable({
  providedIn: 'root'
})
export class Configuration extends BaseService<IConfigData> implements IConfiguration {
  public date = Date.now()
  /**
   * This value seems to be set in the XLS,
   * Variabler D125. As it seems it is not really needed?
   * It is included in the "intjäningstabell" that is
   * pretty lame.
   *
   * Uses List interest - LTV based InternalInterest - this value
   * which renders the same value in every column by design.
   */
  public discount = signal(0.1)

  /**
   * This is used for calculate profit on private loans.
   * This shall be possible to save and restore.
   */
  public internalInterestPrivateLoan = signal(1.44)

  /**
   * This is the interest used for counting on savings accounts (e-spar
   * and Österlen). Originally picked up from Gemensamma. We need to
   * make this update somehow. Gemensamma H163 - H55 (1,34 - 0.05)
   * For now just set the fixed value 1.29
   */
  public interestSavingsAccount = signal(1.29)

  /**
   * This one is for fasträntekonto (i23). It gets its interest from Master.
   * H43 -> and H173 -> it also have a time factor depending on
   * bindings... 4 for 3 mon, 2 for 6 mon.
   *
   * We ignore this for now and calculate all the same way.Since
   * the same way will always return 50 bps for calculations
   */
  public interestFixedInterestSavingsAccount = signal(0.5)

  /**
   * The amount of bps (‱, räntepunkter) needed for a loan
   * to be considered "profitable". This is currently hidden
   * from the UI and not saved / restored.
   */
  public bpsProfitLimit = signal(50)
  /**
   * Converting the interests to an array of objects we
   * can show in tables.
   */
  public listInterests = computed<IInterestRow[]>(() => {
    return [...this.ListInterest().entries()].map(([product, interests]) => {
      const interestObject: Record<number, WritableSignal<number>> = {}

      Object.entries(interests).forEach(([binding, value]) => {
        interestObject[+binding] = signal(value)
      })
      return {product, interests: interestObject}
    })
  })

  public savingsInterests = computed(() => {
    return [...this.SavingsInterests().entries()]
  })

  /**
   * Converts static production prices to dynamic.
   */
  public productionPrices = computed<IInterestRow[]>(() => {
    return [...this.ProductionPrice().entries()].map(([product, interests]) => {
      const interestObject: Record<number, WritableSignal<number>> = {}

      Object.entries(interests).forEach(([binding, value]) => {
        interestObject[+binding] = signal(value)
      })
      return {product, interests: interestObject}
    })
  })

  /**
   * A configurable discount per binding only.
   *
   * All bindings have a configurable discount
   */
  public bindingDiscounts = computed(() => {
    return [[...this.BindingDiscounts().values()]
      .map(value => signal(value))]
  })

  public changes = signal<[string, string | number][]>([])

  public needsSave = computed(() => this.changes().length > 0)

  /**
   * This _should_ be an observale but to easae testing
   * we expose it. Sad but true.
   */
  public saved = new Subject<number>()

  protected override baseUrl = `${environment.apiUrl}/items` as any

  /**
   * Used to load data, first interests later settings.
   */
  private readonly dataService = inject(DataService)

  /**
   * Populated by real interests in the constructor
   */
  private readonly ListInterest = signal(new Map<string, Record<number, number>>([
    ['l750', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}],
    ['l751', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}],
    ['l752', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}],
    ['l753', {3: 1, 12: 2, 24: 4, 36: 6, 48: 8, 60: 0, 84: 14, 120: 12}],
    ['l754', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}],
    ['l755', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}],
    ['l756', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}],
    ['Borgo', {3: 1, 12: 1, 24: 1, 36: 1, 48: 1, 60: 1, 84: 1, 120: 1}]
  ]))

  private readonly SavingsInterests = signal(new Map<string, number>([
    ['i15', 0], // Private banking
    ['i16', 0], // Specialinlångin
    ['i24', 0], // Fasträntekonto 3 mån
    ['i25', 0], // Fasträntekonto 6 mån
    ['i26', 0],// Fasträntekonto 1år
    ['i27', 0],// Fasträntekonto 1,5 år
    ['i28', 0],// Fasträntekonto 2 år
    ['i29', 0],// Fasträntekonto 3 år
    ['i30', 0],// Fasträntekonto 4 år
    ['i31', 0],// Fasträntekonto 5 år
    ['i32', 0],// Fasträntekonto 3 mån auto
    ['i51', 0], // Österlenkonto
    ['i83', 0], // E-spar
    ['i84', 0] // Häst
  ]))

  /**
   * Taken from Excel, shall be saved and loaded.
   *
   * Each row is discount based on "ltv" for each binding.
   *
   * id est, 2 years ltv 70 = 2,87
   */
  private readonly ProductionPrice = signal(new Map<number, Record<number, number>>([
    [3,
      /**
       * We need:
       * 0-50, 50-55, 55-60, 60-65, 65-70, 70-75, 75-80, 80-85, 85-90, 90-100+
       0      1      2      3      4      5      6      7      8      9
       50:    55     60     65     70     75     80     85     90     100 (or rest)
       { 50: 0,  55: , 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}
       */
      {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [12, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [24, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [36, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [48, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [60, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [84, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}],
    [120, {50: 0, 55: 0, 60: 0, 65: 0, 70: 0, 75: 0, 80: 0, 85: 0, 90: 0, 100: 0}]
  ]))

  /**
   * Binding discounts used as primary input to calculations.
   * These can be edited and saved by administrators.
   */
  private readonly BindingDiscounts = signal(new Map<number, number>([
    [3, 0.31],
    [12, 0.35],
    [24, 0.11],
    [36, 0.06],
    [48, 0.15],
    [60, 0.1],
    [84, 0], // Is this zero, they are missing in the XLS
    [120, 0]
  ]))

  /**
   * Name this to something more sensible some other day
   */
  private readonly id = 'profit-hunger-configuration-v2'
  private version = 0

  constructor() {
    super()
    this.load()

    this.dataService.getInterests().subscribe(interests => {
      const l753 = this.ListInterest().get('l753')!
      // Note that we set the borgo first, since they for now have 7 and 10 year
      // Then we overwrite with l753, things to fix here. (80%)
      // RT is updated with support.
      Object.assign(l753, interests.l753)
      const borgo = this.ListInterest().get('Borgo')!
      Object.assign(borgo, interests.borgo)

      // l754 and l756 has same as l753
      this.setInterestWithDiscount('l754', 0)
      this.setInterestWithDiscount('l756', 0)
      // L751 has - 0.1
      this.setInterestWithDiscount('l751', -0.1)
      // These loans have + 0.2
      const others = ['l750', 'l752', 'l755']
      for (const other of others) {
        this.setInterestWithDiscount(other, 0.2)
      }
      // The point of this exercise is to make them 2 decimals
      // Iterate over every loan and evey value and make them fixed.
      [...this.ListInterest().values()].forEach((value) => {
        Object.entries(value).forEach(([key, v]) => {
          value[+key] = Number.parseFloat(v.toFixed(2))
        })
      })
      // Make a copy and update.
      this.ListInterest.set(new Map(this.ListInterest().entries()))

      // Handle savings accounts
      this.SavingsInterests.update(map => {
        Object.entries(interests.fixed)
          .forEach(([k, v]) => map.set(k, v))
        return new Map(map.entries())
      })
    })
  }

  public setCurrentConfig(config: IConfigData) {
    this.version = config.version
    const productionPrices = new Map<number, Record<number, number>>
    Object.entries(config.productionPrices).forEach(([key, value]) => {
      productionPrices.set(+key, value)
    })
    this.ProductionPrice.set(productionPrices)
    const bindingDiscounts = new Map<number, number>()
    config.bindingDiscounts.forEach(discount => {
      bindingDiscounts.set(discount[0], discount[1])
    })
    this.BindingDiscounts.set(bindingDiscounts)
    this.discount.set(config.discount)
    this.internalInterestPrivateLoan.set(config.internalInterestPrivateLoan)
    this.interestSavingsAccount.set(config.interestSavingsAccount)
    this.interestFixedInterestSavingsAccount.set(config.interestFixedInterestSavingsAccount)
    // Reset all changes
    this.changes.set([])
    this.saved.next(config.version)
  }

  /**
   * Save configuration.
   */
  public save(): void {
    this.update(this.id, this.getCurrentConfig())
      .pipe(tap(config => {
        this.version = config.version
        this.saved.next(config.version)
        this.changes.set([])
      })).subscribe()
  }

  public getCurrentConfig(): IConfigData {
    const productionPrices: Record<number, Record<number, number>> = {}
    for (const row of this.productionPrices()) {
      const interests: Record<number, number> = {}
      for (const [k, v] of Object.entries(row.interests)) {
        interests[+k] = v()
      }
      productionPrices[+row.product] = interests
    }

    const bindingDiscounts: [number, number][] = []

    this.bindingDiscounts().forEach(discounts => {
      discounts.forEach((discount, index) =>
        bindingDiscounts.push([fromColumn[index], discount()]))
    })

    return {
      version: this.version,
      id: this.id,
      date: Date.now(),
      productionPrices: productionPrices,
      bindingDiscounts,
      discount: this.discount(),
      internalInterestPrivateLoan: this.internalInterestPrivateLoan(),
      interestSavingsAccount: this.interestSavingsAccount(),
      interestFixedInterestSavingsAccount: this.interestFixedInterestSavingsAccount()
    }
  }

  /**
   * Load the config w/o waiting for the result.
   * @private
   */
  private load() {
    this.read(this.id).subscribe(config => {
      this.setCurrentConfig(config)
    })
  }

  /**
   * Find the best column based on value.
   * @param ltv - Convert e.g. 68 to 65
   */
  public ltvColumn(ltv: number): number {
    // 50:    55     60     65     70     75     80     85     90     100
    const ltvs = [50, 55, 60, 65, 70, 75, 80, 85, 90, 100]
    return ltvs.find(l => ltv <= l) ?? 100
  }

  /**
   * The "Internränta" is list interest - discounts based on binding only.
   */
  public internalInterest(productId: string, binding: number, ltv: number): number {
    // List interest for product and binding
    const listInterest = this.listInterest(productId, binding)

    // Base discount based on binding only
    const bindingDiscount = this.bindingDiscount(binding)

    // The discount based on binding and LTV
    const productionPriceDiff = this.discountOnBindingAndLTV(binding, ltv)

    const totalDiscount = productionPriceDiff + bindingDiscount
    return listInterest - totalDiscount
  }

  /**
   * Get the list interest from the "Dynamic" table of interests
   */
  public listInterest(productId: string, binding: number): number {
    const items = this.listInterests()
    const row = items.find(r => r.product === productId)!
    return row.interests[binding]()
  }

  /**
   * Returns the set discount based on the binding, 3,12... 120 months.
   * This is fetched from the "Dynamic" table.
   *
   * @param binding - 3,12,24 etc.
   */
  public bindingDiscount(binding: number): number {
    const bindingDiscounts = this.bindingDiscounts()
    return bindingDiscounts[0][toColumn[binding]]()
  }

  public productionPriceLtv(binding: number, ltv: number): number {
    const baseProductionPriceRow = this.productionPrices().find(r => r.product === binding)!.interests
    // The discount is calculated as the diff between the base (the highest column) and the actual LTV
    return baseProductionPriceRow[this.ltvColumn(ltv)]()
  }
  /**
   * The discount is calculated based on the difference between the "base" LTV
   * 90+ production price and the production price set for this LTV.
   *
   * Note that this has _nothing_ to do with list prices.
   * @param binding - 3, 12, 24 etc.
   * @param ltv - any number between 0 .. 100, we will meke it a columns for you
   */
  public discountOnBindingAndLTV(binding: number, ltv: number) {
    // The discount based on binding and LTV
    const baseProductionPriceRow = this.productionPrices().find(r => r.product === binding)!.interests
    // The discount is calculated as the diff between the base (the highest column) and the actual LTV
    const productionPriceBase = baseProductionPriceRow[100]()
    const productionPriceLtv = this.productionPriceLtv(binding, ltv)
    return productionPriceBase - productionPriceLtv
  }

  /**
   * Interest for all products except Borgo and L753 is set in relation
   * to L753. This updates the static values in the table to the calculated
   * interest.
   *
   * @param product - Like l753
   * @param discount - the product related discount -0.1 or +0.2
   */
  private setInterestWithDiscount(product: string, discount: number) {
    const l753 = this.ListInterest().get('l753')!
    const loan = this.ListInterest().get(product)!
    Object.entries(l753).forEach(([key, value]) => {
      loan[+key] = value + discount
    })
  }
}
